"use client";

import Link from "next/link";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import { FaChevronRight } from "react-icons/fa";
import { client } from "@/sanity/lib/client";

import { IconRenderer } from "./IconRenderer";

export interface HelpCard {
  title?: string;
  description?: string;
  link?: string;
  iconName?: string;
}

export interface CategoryCard {
  _id?: string;
  title?: string;
  subtitle?: string;
  slug?: {
    current?: string;
  };
  seoDescription?: string;
  largeBoxImageUrl?: string;
}

export interface PostCard {
  _id?: string;
  headline?: string;
  title?: string;
  subtitle?: string;
  slug?:
    | string
    | {
        current?: string;
      };
  navtitle?: string;
  categorySlug?: string;
  isPost?: boolean;
  isExternal?: boolean;
  openInNewTab?: boolean;
}

export interface WhoWeHelpProps {
  heading?: string;
  description?: string;
  cards?: HelpCard[];
  largeCards?: CategoryCard[];
  smallCards?: PostCard[];
  defaultSmallCards?: PostCard[];
}

function renderIcon(iconName?: string) {
  return (
    <IconRenderer
      name={iconName || "user-group"}
      className="size-10 text-violet-950"
    />
  );
}

/**
 * Who We Help Section - 4 cards for self-identification
 * Enhanced design matching Product Showcase and Trust Strip sections
 */
export default function WhoWeHelp({
  heading = "Hair Loss & Hair Restoration Care for Different Needs",
  description = undefined,
  cards,
  largeCards,
  smallCards,
  defaultSmallCards,
}: WhoWeHelpProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(
    null
  );
  const [dynamicSmallCards, setDynamicSmallCards] = useState<PostCard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Use default small cards initially
  useEffect(() => {
    if (defaultSmallCards && defaultSmallCards.length > 0) {
      setDynamicSmallCards(defaultSmallCards);
    } else if (smallCards && smallCards.length > 0) {
      setDynamicSmallCards(smallCards);
    }
  }, [defaultSmallCards, smallCards]);

  // Fetch posts for hovered or selected category
  useEffect(() => {
    const categoryId = hoveredCategoryId || selectedCategoryId;
    if (!categoryId) {
      // Reset to default small cards when no category is hovered/selected
      if (defaultSmallCards && defaultSmallCards.length > 0) {
        setIsAnimating(true);
        setTimeout(() => {
          setDynamicSmallCards(defaultSmallCards);
          setTimeout(() => setIsAnimating(false), 100);
        }, 150);
      } else if (smallCards && smallCards.length > 0) {
        setIsAnimating(true);
        setTimeout(() => {
          setDynamicSmallCards(smallCards);
          setTimeout(() => setIsAnimating(false), 100);
        }, 150);
      }
      return;
    }

    const fetchCategoryPosts = async () => {
      setIsLoading(true);
      try {
        // Get category with featuredPosts and slug
        // featuredPosts can contain post references or customLink objects
        // Filter posts where showInSmallBox is true
        const categoryQuery = `*[_type == "category" && _id == $categoryId][0] {
          "slug": slug.current,
          featuredPosts[]{
            _type == "reference" => @->{
              _id,
              _type,
              headline,
              title,
              navtitle,
              subtitle,
              "slug": slug.current,
              showInSmallBox
            },
            _type != "reference" => {
              _type,
              label,
              url,
              isExternal,
              openInNewTab,
              description
            }
          }
        }`;
        const category = await client.fetch(categoryQuery, {
          categoryId: categoryId,
        });

        // Process featuredPosts - extract post references and custom links
        // Filter out posts where showInSmallBox is false
        const featuredItems = (category?.featuredPosts || [])
          .map((item: any, idx: number) => {
            // Check if it's a post (has _id and _type === "post")
            if (item._type === "post" && item._id) {
              // Only include if showInSmallBox is true
              if (item.showInSmallBox !== true) {
                return null;
              }
              return {
                _id: item._id,
                headline: item.headline,
                title: item.title,
                navtitle: item.navtitle,
                subtitle: item.subtitle,
                slug: item.slug,
                categorySlug: category?.slug,
                isPost: true,
              };
            }
            // Check if it's a custom link (has url and label)
            if (item.url && item.label) {
              return {
                _id: `custom-${item.url}-${idx}`,
                headline: item.label,
                title: item.label,
                navtitle: item.label,
                slug: item.url,
                isExternal: item.isExternal,
                openInNewTab: item.openInNewTab,
                isPost: false,
              };
            }
            return null;
          })
          .filter(Boolean);

        // If no featured posts found, fallback to default small cards
        setIsAnimating(true);
        // Small delay to trigger fade-out
        setTimeout(() => {
          if (featuredItems.length === 0) {
            if (defaultSmallCards && defaultSmallCards.length > 0) {
              setDynamicSmallCards(defaultSmallCards);
            } else if (smallCards && smallCards.length > 0) {
              setDynamicSmallCards(smallCards);
            }
          } else {
            setDynamicSmallCards(featuredItems);
          }
          // Trigger fade-in after content update
          setTimeout(() => setIsAnimating(false), 100);
        }, 150);
      } catch (error) {
        console.error("Error fetching category featured posts:", error);
        // Fallback to default small cards on error
        setIsAnimating(true);
        setTimeout(() => {
          if (defaultSmallCards && defaultSmallCards.length > 0) {
            setDynamicSmallCards(defaultSmallCards);
          } else if (smallCards && smallCards.length > 0) {
            setDynamicSmallCards(smallCards);
          }
          setTimeout(() => setIsAnimating(false), 100);
        }, 150);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategoryPosts();
  }, [hoveredCategoryId, selectedCategoryId, defaultSmallCards, smallCards]);

  // Use new structure if provided, otherwise fall back to old structure or defaults
  const defaultCards = [
    {
      iconName: "FaHeadSideVirus",
      title: "Scalp Hair Loss",
      description: "Hairline • Crown • Baldness",
      link: "/blog/hair-loss",
    },
    {
      iconName: "FaUser",
      title: "Facial Hair Concerns",
      description: "Beard • Eyebrow • Moustache",
      link: "/blog/facial-hair",
    },
    {
      iconName: "FaTools",
      title: "Corrective / Failed Transplants",
      description: "Repair & refinement",
      link: "/blog/repair",
    },
    {
      iconName: "FaFlask",
      title: "Non-Surgical Options",
      description: "PRP • GFC • SMP",
      link: "/blog/non-surgical",
    },
  ];

  const largeBoxes =
    largeCards && largeCards.length > 0
      ? largeCards
      : cards && cards.length > 0
        ? cards
        : defaultCards;

  const handleLargeCardClick = (categoryId?: string) => {
    if (categoryId) {
      // If clicking the same category, reset to default small cards
      if (selectedCategoryId === categoryId) {
        setSelectedCategoryId(null);
        if (defaultSmallCards && defaultSmallCards.length > 0) {
          setDynamicSmallCards(defaultSmallCards);
        } else if (smallCards && smallCards.length > 0) {
          setDynamicSmallCards(smallCards);
        }
      } else {
        setSelectedCategoryId(categoryId);
      }
    }
  };

  // Render small cards grid (shared for mobile under active card + desktop)
  const renderSmallCardsGrid = () => {
    if (!dynamicSmallCards || dynamicSmallCards.length === 0) return null;
    const parentColor = getActiveParentColor();
    const smallCardBg =
      parentColor != null
        ? getSmallCardBgColor(parentColor)
        : defaultBgColor;
    return (
      <div
        className={`grid gap-2 grid-cols-2 md:grid-cols-4 transition-all duration-500 ease-in-out ${
          isAnimating ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
        }`}
      >
        {isLoading ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            Loading...
          </div>
        ) : (
          dynamicSmallCards.map((card, index) => {
            const postTitle =
              card.subtitle || card.headline || card.navtitle || card.title;
            const postSlug =
              typeof card.slug === "string"
                ? card.slug
                : card.slug?.current || undefined;

            let postLink = "#";
            if (card.isPost !== false && postSlug) {
              const categorySlug =
                card.categorySlug ||
                (
                  largeBoxes.find(
                    (c: any) => c._id === selectedCategoryId
                  ) as CategoryCard
                )?.slug?.current ||
                "";
              postLink =
                categorySlug && postSlug
                  ? `/${categorySlug}/${postSlug}`
                  : postSlug
                    ? `/${postSlug}`
                    : "#";
            } else if (card.isPost === false && postSlug) {
              postLink =
                postSlug.startsWith("http") || postSlug.startsWith("/")
                  ? postSlug
                  : `/${postSlug}`;
            }

            const linkProps = card.openInNewTab
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {};

            return (
              <Link
                key={card._id || `small-${index}`}
                href={postLink}
                {...linkProps}
                className="group flex flex-col rounded-2xl shadow-sm transition-all hover:shadow-lg h-[100px]"
                style={{ backgroundColor: smallCardBg }}
              >
                <div className="flex items-center justify-between px-4 h-full">
                  <h3 className="text-left text-[20px] font-bold text-gray-900">
                    {postTitle}
                  </h3>
                  <FaChevronRight className="size-4 text-gray-600 flex-shrink-0" />
                </div>
              </Link>
            );
          })
        )}
      </div>
    );
  };

  const cardColors = ["#6E8CFB", "#BF124D", "#76153C", "#456882"];
  const defaultBgColor = "#F0EDD8";

  // Lighter tint of parent color for small cards (mix hex with white)
  const getSmallCardBgColor = (parentHex: string, mixWithWhite = 0.72): string => {
    const hex = parentHex.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const wr = Math.round(r + (255 - r) * mixWithWhite);
    const wg = Math.round(g + (255 - g) * mixWithWhite);
    const wb = Math.round(b + (255 - b) * mixWithWhite);
    return `#${wr.toString(16).padStart(2, "0")}${wg.toString(16).padStart(2, "0")}${wb.toString(16).padStart(2, "0")}`;
  };

  const getActiveParentColor = (): string | null => {
    const categoryId = hoveredCategoryId || selectedCategoryId;
    if (!categoryId) return null;
    const index = largeBoxes.findIndex(
      (c: CategoryCard | HelpCard) => (c as CategoryCard)._id === categoryId
    );
    if (index < 0) return null;
    return index === 1 ? "#00A26B" : cardColors[index % cardColors.length];
  };

  const renderLargeCard = (
    card: CategoryCard | HelpCard,
    index: number,
    isActive?: boolean
  ) => {
    const categoryId = (card as CategoryCard)._id;
    const categoryCard = card as CategoryCard;
    const categoryTitle =
      categoryCard.subtitle || categoryCard.title || (card as HelpCard).title;
    const imageUrl = categoryCard.largeBoxImageUrl;
    const hoverColor =
      index === 1 ? "#00A26B" : cardColors[index % cardColors.length];
    const bgColor = isActive ? hoverColor : defaultBgColor;

    return (
      <button
        key={categoryId || index}
        onClick={() => categoryId && handleLargeCardClick(categoryId)}
        className="group flex w-full flex-col rounded-2xl shadow-sm transition-all hover:shadow-lg h-[100px] md:h-[200px] relative overflow-hidden"
        style={{
          backgroundColor: bgColor,
          transition: "background-color 0.2s ease, color 0.2s ease",
        }}
      >
        {imageUrl && (
          <div className="absolute inset-0 flex items-center justify-end md:justify-center pr-4 md:pr-0 z-0">
            <div className="relative w-20 h-20 md:w-32 md:h-32">
              <Image
                src={imageUrl}
                alt={categoryTitle || ""}
                fill
                className="object-contain"
              />
            </div>
          </div>
        )}
        <div className="flex items-center md:items-start justify-between px-4 h-full md:pt-4 relative z-10">
          <h3
            className={`text-left text-[20px] font-bold transition-colors duration-200 ${isActive ? "text-white" : "text-gray-900 group-hover:text-white"}`}
          >
            {categoryTitle}
          </h3>
          <FaChevronRight
            className={`size-4 flex-shrink-0 transition-colors duration-200 md:mt-0.5 ${isActive ? "text-white" : "text-gray-600 group-hover:text-white"}`}
          />
        </div>
      </button>
    );
  };

  return (
    <section className="pt-0 pb-16 md:pt-0 md:pb-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto w-full space-y-2">
          {largeBoxes && largeBoxes.length > 0 && (
            <>
              {/* Mobile: small cards only on tap/click (no hover); active state from selection only */}
              <div className="space-y-2 md:hidden">
                {largeBoxes.map((card, index) => {
                  const categoryId = (card as CategoryCard)._id;
                  const isActive = selectedCategoryId === categoryId;
                  return (
                    <div key={categoryId || index} className="space-y-2">
                      {renderLargeCard(card, index, isActive)}
                      {isActive && renderSmallCardsGrid()}
                    </div>
                  );
                })}
              </div>

              {/* Desktop: 4 large cards row, then small cards row; one hover zone so moving to small cards keeps hover */}
              <div
                className="hidden md:block space-y-2"
                onMouseLeave={() => setHoveredCategoryId(null)}
              >
                <div className="grid gap-2 grid-cols-4">
                  {largeBoxes.map((card, index) => {
                    const categoryId = (card as CategoryCard)._id;
                    const isActive = hoveredCategoryId === categoryId;
                    return (
                      <div
                        key={categoryId || index}
                        onMouseEnter={() =>
                          categoryId && setHoveredCategoryId(categoryId)
                        }
                      >
                        {renderLargeCard(card, index, isActive)}
                      </div>
                    );
                  })}
                </div>
                {renderSmallCardsGrid()}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
